/**
 * LicenseGate Component
 * Blocks portal access for unlicensed tenants.
 * Shows a helpful "License Not Valid" page with a copyable Tenant ID
 * so the user can share it with their admin to get a license provisioned.
 */

import { useLicense } from '../../hooks/useLicense';
import { LicenseBanner } from './LicenseBanner';
import { getCurrentTenantId } from '../../services/licenseService';
import { LicenseNotFoundCard } from './LicenseNotFoundCard';
import { StatusSecondaryButton } from '../layout/StatusScreen';

interface LicenseGateProps {
  children: React.ReactNode;
}

export function LicenseGate({ children }: LicenseGateProps) {
  const { license, isLoading, isValid, error } = useLicense();

  // Loading state
  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50'>
        <div className='text-center'>
          <div className='inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-purple-600 border-r-transparent'></div>
          <p className='mt-4 text-lg text-gray-700'>Verifying license...</p>
        </div>
      </div>
    );
  }

  // Invalid or no license — show helpful page with copyable Tenant ID
  if (!isValid || !license || license.status === 'Invalid') {
    // Get tenantId from license if available, otherwise from MSAL directly
    const tenantId = license?.tenantId || getCurrentTenantId() || '';

    return (
      <LicenseNotFoundCard
        tenantId={tenantId}
        subtitle={
          error ||
          'Your organisation does not have an active Content Trust Dashboard license.'
        }
        actions={
          <StatusSecondaryButton full onClick={() => window.location.reload()}>
            🔄 Refresh Page
          </StatusSecondaryButton>
        }
      />
    );
  }

  // Valid license (Active or Expired) — render children with banner if Expired
  return (
    <>
      <LicenseBanner />
      {children}
    </>
  );
}
